import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { getQueryParam } from '@utils/query';
import { useAuthentication } from '@context/businessContainer';
import { useSessionStorage } from '@monofront/business/hooks';
import { useRouter } from 'next/router';
import invariant from 'tiny-invariant';
import { useDiscountCode } from '@hooks/useDiscountCode';

interface IReferralContext {
  referrerCode: string | null;
  setReferrerCode: (token: string) => void;
  clearContext: () => void;
  isFromReferral: boolean;
}

const ReferralContext = createContext<IReferralContext | null>(null);

const useReferrerCode = () => {
  const [discountCode, setDiscountCode, meta] = useDiscountCode();
  const codeInParams =
    getQueryParam('referrerCode') ?? getQueryParam('discountcode') ?? '';

  useEffect(() => {
    if (codeInParams) {
      setDiscountCode(codeInParams);
    }
  }, [codeInParams]);

  return [discountCode, setDiscountCode, meta] as const;
};

/**
 * This context handles referral code redeem automatically base on token and referee email being fulfilled
 */
const ReferralContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const router = useRouter();
  const { isMember } = useAuthentication();
  const [referrerCode, setReferrerCode, referrerCodeMeta] = useReferrerCode();

  const [
    hasVisitedReferralPageFromSession,
    setHasVisitedReferralPageFromSession,
    hasVisitedReferralPageFromSessionMeta,
  ] = useSessionStorage('hasVisitedReferralPage', 'false');

  const hasVisitedReferralPage =
    JSON.parse(hasVisitedReferralPageFromSession) === true;

  useEffect(
    function cleanupWhenMember() {
      if (isMember) {
        referrerCodeMeta.deleteItem();
        hasVisitedReferralPageFromSessionMeta.deleteItem();
      }
    },
    [isMember, referrerCodeMeta, hasVisitedReferralPageFromSessionMeta],
  );

  const handleClearContext = useCallback(() => {
    referrerCodeMeta.deleteItem();
    setHasVisitedReferralPageFromSession('false');
  }, [referrerCodeMeta, setHasVisitedReferralPageFromSession]);

  useEffect(() => {
    //Whenever redeem page is visited by user, set variable hasVisitedReferralPage to true
    if (
      !hasVisitedReferralPage &&
      router.pathname === '/a/refer-a-friend/redeem'
    ) {
      setHasVisitedReferralPageFromSession('true');
    }
  });

  return (
    <ReferralContext.Provider
      value={useMemo(
        () => ({
          referrerCode,
          setReferrerCode,
          clearContext: handleClearContext,
          isFromReferral: hasVisitedReferralPage,
        }),
        [
          referrerCode,
          setReferrerCode,
          handleClearContext,
          hasVisitedReferralPage,
        ],
      )}
    >
      {children}
    </ReferralContext.Provider>
  );
};

const useReferralContext = () => {
  const ctx = useContext(ReferralContext);

  invariant(ctx, 'ReferralContext should be present upper in the tree');

  return ctx;
};

export { ReferralContextProvider, ReferralContext, useReferralContext };
